import type { ContestEntry } from '../contest-entries';
import type { ContestViewEntryIds } from './contest-view-entry-ids.model';
import {
  EntryIdFilterOption,
  EntryIdSortOption,
} from './contest-view-entry-ids.model';

export interface GenerateContestViewEntryIdsOptions {
  readonly contestId: number;
  readonly entryIds: readonly number[];
  // Only set for owner-scoped docs (My Entries) or entrant searches
  // The default (All Entries) filter omits it
  readonly entryOwnerIds?: readonly number[];
  readonly entryNumbers?: readonly number[];
  readonly filter?: EntryIdFilterOption;
  readonly sort?: EntryIdSortOption;
  readonly quickView?: boolean;
}

export function generateContestViewEntryIdsObject({
  contestId,
  entryIds,
  entryOwnerIds,
  entryNumbers = entryIds.map((_, index) => index + 1),
  filter = EntryIdFilterOption.DEFAULT,
  sort = EntryIdSortOption.DEFAULT,
  quickView = false,
}: GenerateContestViewEntryIdsOptions): ContestViewEntryIds {
  return {
    id: `${contestId}_${filter}_${sort}_${quickView}_${JSON.stringify(
      entryOwnerIds ?? null,
    )}`, // from transformer
    contestId,
    entryIds,
    entryNumbers,
    filter,
    sort,
    quickView,
    // Omit when absent so the doc matches the unscoped All Entries filter.
    ...(entryOwnerIds !== undefined ? { entryOwnerIds } : {}),
  };
}

// --- Mixins ---
export function withEntriesForContestViewEntryIds(
  entries: readonly ContestEntry[],
): Pick<GenerateContestViewEntryIdsOptions, 'entryIds'> {
  // All Entries is unscoped; owner-scoped docs pass their own entryOwnerIds.
  return {
    entryIds: entries.map(entry => entry.id),
  };
}
